import logError from 'src/data/services/logError';
import React, { useMemo, useState, useRef } from 'react';
import {
	Alert,
	Text,
	View,
	TouchableOpacity,
	Platform,
	StyleSheet,
	Modal,
	LayoutAnimation,
	ActivityIndicator,
	TextInput as RNTextInput,
} from 'react-native';
import { afterInteractions } from 'src/core/utils/afterInteractions';
import type { ViewStyle } from 'react-native';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Paragraph from 'src/presentation/components/atoms/Paragraph';
import Translations from 'src/core/utils/Translations';
import errorHandler from 'src/core/utils/errorHandler';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useEditModeStore } from 'src/domain/stores/useEditModeStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useAddEventGate } from 'src/presentation/hooks/useAddEventGate';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';

interface TopicusCtaProps {
	label: string;
	onQrScanPress?: () => void;
}

function TopicusCta({ label, onQrScanPress }: TopicusCtaProps) {
	const eventStore = useEventStore();
	const editMode = useEditModeStore();
	const navigationStore = useNavigationStore();

	const contentWidth = useContentWidth();

	// Decision D8: modals stay inside the app's column, so the 80%-wide dialog is 80% of the
	// column and not of a 1440px browser window.
	const sizedStyles = useMemo(
		(): { modalOverlay: ViewStyle } => ({
			modalOverlay: {
				width: contentWidth,
			},
		}),
		[contentWidth],
	);

	const [eventCode, setEventCode] = useState('');
	const [codePromptVisible, setCodePromptVisible] = useState(false);
	const [textInputSelected, setTextInputSelected] = useState(false);

	const prevEventCountRef = useRef(eventStore.eventData.length);
	const prevIsLoadingRef = useRef(eventStore.isAddingEvent);

	if (Platform.OS === 'ios') {
		const eventCountChanged = prevEventCountRef.current !== eventStore.eventData.length;
		const isLoadingChanged = prevIsLoadingRef.current !== eventStore.isAddingEvent;
		if (eventCountChanged || isLoadingChanged) {
			LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
			prevEventCountRef.current = eventStore.eventData.length;
			prevIsLoadingRef.current = eventStore.isAddingEvent;
		}
	}

	const openAccessEventChoice = () => {
		// A browser gets the code entry straight away. The three-button choice runs through React
		// Native's Alert, which is an empty function on web, and the code entry behind it uses
		// Alert.prompt -- iOS only. So the button did nothing at all there. Nothing is lost:
		// scanning stays reachable from the camera tab.
		if (Platform.OS === 'web') {
			showCodePrompt();

			return;
		}

		// A browser never reaches this: the web branch above goes straight to the code entry, because
		// scanning needs a camera tab and the code entry behind the second button is Alert.prompt,
		// which is iOS only.
		// eslint-disable-next-line no-restricted-syntax -- native-only, guarded above.
		Alert.alert(
			Translations.alertAccessEventTitle,
			Translations.alertAccessEventMessage,
			[
				{
					text: Translations.alertScanQr,
					onPress: () => {
						onQrScanPress!();
					},
				},
				{
					text: Translations.alertEnterCode,
					onPress: () => (Platform.OS === 'ios' ? showIosInputAlert() : showCodePrompt()),
				},
				{
					text: Translations.buttonCancel,
					style: 'cancel',
				},
			],
			{ cancelable: true },
		);
	};

	const { requestAddEvent } = useAddEventGate({ onOpenChoice: openAccessEventChoice });

	function showIosInputAlert() {
		// Alert.prompt is iOS only, and the caller only reaches it on iOS; web and Android use
		// showCodePrompt().
		// eslint-disable-next-line no-restricted-syntax -- iOS-only, guarded by the caller.
		Alert.prompt(Translations.alertEventCodeTitle, Translations.alertEventCodeMessage, [
			{ text: Translations.buttonCancel, style: 'cancel' },
			{
				text: Translations.buttonOk,
				onPress: (code: string | undefined) => {
					editMode.reset();
					// Defer until the prompt's dismiss animation finishes: otherwise a resulting error
					// alert (e.g. invalid code) is presented mid-dismissal and iOS silently drops it.
					afterInteractions(() => addEvent(code!));
				},
			},
		]);
	}

	function showCodePrompt() {
		setCodePromptVisible(true);
	}

	function addEvent(code: string) {
		if (navigationStore.isConnected) {
			if (code.length) {
				eventStore.addEvent(code).catch(logError);
				setEventCode('');
			} else {
				errorHandler(Translations.errorDefaultSubject, Translations.errorEmptyEventCode);
			}
		} else {
			errorHandler(Translations.errorAddEventOffline, null);
		}
	}

	return (
		<View style={styles.container}>
			<TouchableOpacity
				disabled={eventStore.isAddingEvent}
				style={[
					styles.cta,
					{
						marginTop: eventStore.eventData.length ? 0 : 24,
					},
				]}
				onPress={requestAddEvent}
			>
				<Text style={styles.label}>{(label ?? '').toUpperCase()}</Text>
				{eventStore.isAddingEvent === true ? (
					<ActivityIndicator
						color={Colors.white}
						size="small"
						style={{
							width: 16,
							height: 16,
							marginLeft: Dimens.margin15,
							marginRight: Dimens.margin15,
						}}
					/>
				) : null}
			</TouchableOpacity>

			{/* Android code-invoer dialoog */}
			<Modal
				animationType="fade"
				transparent={true}
				visible={codePromptVisible}
				onRequestClose={() => {
					setCodePromptVisible(false);
					setEventCode('');
				}}
			>
				<View style={[styles.modalOverlay, sizedStyles.modalOverlay]}>
					<View style={styles.modalContainer}>
						<Text style={styles.modalTitle}>{Translations.alertAddEventTitle}</Text>
						<Paragraph text={Translations.alertAddEventDescription} />
						<RNTextInput
							placeholder={Translations.placeholderEventCode}
							style={[
								styles.modalInput,
								{
									borderBottomWidth: textInputSelected ? 1.5 : 1,
									borderColor: textInputSelected ? Colors.secondary : Colors.lightGrey,
								},
							]}
							value={eventCode}
							onChangeText={(code) => setEventCode(code)}
							onEndEditing={() => setTextInputSelected(false)}
							onFocus={() => setTextInputSelected(true)}
						/>
						<View style={styles.modalActions}>
							<TouchableOpacity
								style={styles.modalBtn}
								onPress={() => {
									setCodePromptVisible(false);
									setEventCode('');
								}}
							>
								<Text style={[styles.modalBtnText, { color: Colors.lightGrey }]}>{Translations.buttonCancelUpper}</Text>
							</TouchableOpacity>
							<TouchableOpacity
								style={styles.modalBtn}
								onPress={() => {
									addEvent(eventCode);
									setCodePromptVisible(false);
									editMode.reset();
								}}
							>
								<Text style={[styles.modalBtnText, { color: Colors.secondary }]}>{Translations.buttonAddUpper}</Text>
							</TouchableOpacity>
						</View>
					</View>
				</View>
			</Modal>
		</View>
	);
}

export default TopicusCta;

const styles = StyleSheet.create({
	container: {
		justifyContent: 'center',
		alignItems: 'center',
	},
	label: {
		fontFamily: Fonts.montserratRegular,
		color: Colors.white,
	},
	cta: {
		paddingTop: 17,
		paddingBottom: 17,
		paddingLeft: Dimens.padding25,
		paddingRight: Dimens.padding25,
		backgroundColor: Colors.secondary,
		borderRadius: 1,
		flexDirection: 'row',
	},
	modalOverlay: {
		alignSelf: 'center',
		flex: 1,
		backgroundColor: Colors.overlay50,
		justifyContent: 'center',
		alignItems: 'center',
	},
	modalContainer: {
		backgroundColor: Colors.white,
		borderRadius: 4,
		padding: Dimens.padding24,
		width: '80%',
		elevation: 8,
	},
	modalTitle: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize18,
		marginBottom: Dimens.margin12,
		color: Colors.black,
	},
	modalInput: {
		marginTop: 21,
		height: 36,
		width: '100%',
	},
	modalActions: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		marginTop: Dimens.margin24,
		gap: 8,
	},
	modalBtn: {
		paddingHorizontal: Dimens.padding8,
		paddingVertical: Dimens.padding4,
	},
	modalBtnText: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize14,
	},
});
